// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { Option } from "@insite/client-framework/Types/FieldDefinition";
import { TabDefinition } from "@insite/client-framework/Types/TabDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import useCurrentBreakPointIndex from "@store/Framework/Common/Hooks/useCurrentBreakpointIndex";
import fieldDefinitionBuilder from "@store/Framework/Common/Utilities/FieldDefinitionBuilder/fieldDefinitionBuilder";
import { responsiveCss } from "@store/Framework/Common/Utilities/FieldDefinitionBuilder/responsiveCss";
import useCategoryListCategories from "@store/Framework/Store/Data/Categories/useCategoryListCategories";
import { FieldDefinitionBuilderOptions, HasResponsive } from "@store/Framework/Types/FieldDefinitionBuilderTypes";
import { FoundationFieldDefinition } from "@store/Framework/Types/FoundationFieldDefinition";
import CategoryAvatar from "@store/Library/Components/Common/CategoryAvatar";
import colors from "@store/Themes/common/styleguide/config/colors";
import React, { useContext } from "react";
import { css } from "styled-components";

enum fields {
    numberOfItemsPerRow = "numberOfItemsPerRow",

    rowGap = "rowGap",
    columnGap = "columnGap",
    title = "title",
    titlePosition = "titlePosition",
    imageSize = "imageSize",
    containerWidth = "containerWidth",
    containerHeight = "containerHeight",
    containerAlignment = "containerAlignment",
    avatarTransitionColor = "avatarTransitionColor",
}

interface CategoryAvatarListStyles {
    container?: InjectableCss;
    titleText?: TypographyPresentationProps;
}

const styles: CategoryAvatarListStyles = {
    titleText: {
        variant: "h2",
    },
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
        `,
    },
};
interface CategoryAvatarListProps extends WidgetProps {
    fields: {
        [fields.numberOfItemsPerRow]: number;
        [fields.rowGap]: number;
        [fields.columnGap]: number;
        [fields.title]: string;
        [fields.titlePosition]: TitlePosition;
        [fields.imageSize]: number;
        [fields.containerWidth]: number;
        [fields.containerHeight]: number;
        [fields.containerAlignment]: JustifiedAlignment;
        [fields.avatarTransitionColor]: string;
    } & FieldDefinitionBuilderOptions;
}

const CategoryAvatarList: React.FC<CategoryAvatarListProps> = ({ fields }) => {
    const categories = useCategoryListCategories();
    const currentBreakpoint = useCurrentBreakPointIndex();
    const currentBreakPointField = fields.responsive?.[currentBreakpoint ?? 0]?.fields;
    const avatarTransitionColor: string = currentBreakPointField?.avatarTransitionColor || fields.avatarTransitionColor;
    const rowGap: string = currentBreakPointField?.rowGap || fields.rowGap;
    const columnGap: string = currentBreakPointField?.columnGap || fields.columnGap;
    const title: string = currentBreakPointField?.title || fields.title;
    const titlePosition: TitlePosition = currentBreakPointField?.titlePosition || fields.titlePosition;
    const containerHeight: string = currentBreakPointField?.containerHeight || fields.containerHeight;
    const containerWidth: string = currentBreakPointField?.containerWidth || fields.containerWidth;
    const imageSize: string = currentBreakPointField?.imageSize || fields.imageSize;
    const numberOfItemsPerRow: number = currentBreakPointField?.numberOfItemsPerRow || fields.numberOfItemsPerRow;
    const gridLayoutStyles: InjectableCss = {
        css: css`
            display: grid;
            grid-template-columns: repeat(${numberOfItemsPerRow || 5}, minmax(0, 1fr));
            row-gap: ${rowGap};
            column-gap: ${columnGap};
        `,
    };

    return (
        <StyledWrapper as="section" {...styles.container}>
            {title && <Typography {...styles.titleText}>{title}</Typography>}
            <StyledWrapper {...gridLayoutStyles}>
                {categories.map(category => (
                    <CategoryAvatar
                        transitionColor={avatarTransitionColor}
                        titlePosition={titlePosition}
                        key={category.id}
                        category={category}
                        containerHeight={containerHeight}
                        containerWidth={containerWidth}
                        imageSize={imageSize}
                        alignment={fields.containerAlignment}
                    />
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export type AvatarDisplaySize = "small" | "medium" | "large";
export type JustifiedAlignment =
    | "center"
    | "flex-start"
    | "flex-end"
    | "space-between"
    | "space-around"
    | "space-evenly";
export type TitlePosition = "above" | "below";
const displayOptions: Option<AvatarDisplaySize>[] = [
    { displayName: "Small", value: "small" },
    { displayName: "Medium", value: "medium" },
    { displayName: "Large", value: "large" },
];
const justifiedAlignmentOptions: Option<JustifiedAlignment>[] = [
    { displayName: "Space Between", value: "space-between" },
    { displayName: "Space Around", value: "space-around" },
    { displayName: "Space Evenly", value: "space-evenly" },
];
const rowGapFieldDefinition = getCssUnitField(fields.rowGap);
const columnGapFieldDefinition = getCssUnitField(fields.columnGap);
const imageSizeFieldDefinition = getCssUnitField(fields.imageSize);
const containerWidthFieldDefinition = getCssUnitField(fields.containerWidth);
const containerHeightFieldDefinition = getCssUnitField(fields.containerHeight);
const styleTab: TabDefinition = {
    displayName: "Style",
    sortOrder: 10,
};

const widgetModule: WidgetModule<FoundationFieldDefinition> = {
    component: CategoryAvatarList,
    definition: {
        group: "Basic",
        icon: "AvatarList",
        fieldDefinitions: [
            ...fieldDefinitionBuilder([
                {
                    name: fields.numberOfItemsPerRow,
                    displayName: "Number of Items Per Row",
                    editorTemplate: "IntegerField",
                    defaultValue: 5,
                    fieldType: "General",
                    sortOrder: 0,
                },
                {
                    displayName: "Image Size",
                    ...imageSizeFieldDefinition.definition,
                    defaultValue: "15rem",
                    tab: styleTab,
                },
                {
                    displayName: "Container Width",
                    ...containerWidthFieldDefinition.definition,
                    defaultValue: "15rem",
                    tab: styleTab,
                },
                {
                    displayName: "Container Height",
                    ...containerHeightFieldDefinition.definition,
                    defaultValue: "20rem",
                },
                {
                    name: fields.avatarTransitionColor,
                    displayName: "Circle Highlight Color",
                    editorTemplate: "ColorPickerField",
                    defaultValue: colors?.secondary?.main || "",
                    fieldType: "General",
                    sortOrder: 1,
                },
                {
                    displayName: "Row gap",
                    ...rowGapFieldDefinition.definition,
                },
                {
                    displayName: "Column gap",
                    ...columnGapFieldDefinition.definition,
                },
                {
                    name: fields.title,
                    displayName: "Title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    fieldType: "General",
                    sortOrder: 3,
                },
                {
                    name: fields.containerAlignment,
                    displayName: "Container Alignment",
                    editorTemplate: "DropDownField",
                    defaultValue: "space-evenly",
                    fieldType: "General",
                    sortOrder: 4,
                    options: justifiedAlignmentOptions,
                },

                {
                    name: fields.titlePosition,
                    displayName: "Title Position",
                    editorTemplate: "DropDownField",
                    defaultValue: "above",
                    fieldType: "General",
                    sortOrder: 4,
                    options: [
                        { displayName: "Above", value: "above" },
                        { displayName: "Below", value: "below" },
                    ],
                },
            ]),
        ],
    },
};

export default widgetModule;
